<template>
  <v-dialog v-model="open" max-width="700px" persistent class="add-note">
    <v-card class="pt-2">
      <v-card-text>
        <div class="d-flex align-center my-2"> 
          <span class="text-h5">Add Note</span>
          <v-spacer></v-spacer>
          <template v-if="displayDepartmentSelection">
            <v-select dense outlined class="add-note__select mr-2" label="Template" placeholder="Select an option" v-model="templateId" :items="getNoteTemplates" hide-details item-text="template_name" item-value="id" @change="selectTemplate"></v-select>
            <v-select dense outlined class="add-note__select" label="Note type" placeholder="Select an option" v-model="type" :items="getProductNotesTypes" :disabled="isTemplate" hide-details :error-messages="typeSelectError" @blur="$v.type.$touch()"></v-select>
          </template>
        </div>

        <div>
          <span v-if="notesType === 'product'" class="d-flex justify-end text-caption error--text bold--text mt-n2 mb-2 mr-6">{{ typeSelectError[0] }}</span>
        </div>

        <v-textarea outlined label="Text Input" placeholder="Type here..." v-model="note" :error-messages="noteError" @blur="$v.note.$touch()" @input="$v.note.$touch()"></v-textarea>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn color="primary" small :loading="addNoteLoading" :disabled="disableSaveButton" @click="saveNote()">Save</v-btn>
        <v-btn color="primary" small outlined class="ml-3" @click="closeDialog()">Cancel</v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
  import { validationMixin } from 'vuelidate';
  import { required } from 'vuelidate/lib/validators';
  import { mapGetters, mapActions } from 'vuex';

  export default {
    mixins: [validationMixin],
    
    props: {
      open: Boolean,
      id: String,
      notesType: String,
    },

    data() {
      return {
        type: '',
        templateId: '',
        templateIdRef: '',
        note: '',
        isTemplate: false,
        addNoteLoading: false,
        addNoteError: false,
      }
    },

    computed: {
      ...mapGetters('accounts', ['getAddAccountNoteError']),
      ...mapGetters('productNotes', ['getNoteTemplates', 'getProductNotesTypes', 'getAddProductNoteError']),
      ...mapGetters('tickets', ['getAddTicketNoteError']),
      ...mapGetters('retention', ['getCreateRetentionLeadNoteError']),

      typeSelectError() {
        const errors = [];
        if (!this.$v.type.$dirty) return errors;
        !this.$v.type.required && errors.push("Please select department");
        return errors;
      },

      noteError() {
        const errors = [];
        if (!this.$v.note.$dirty) return errors;
        !this.$v.note.required && errors.push("Note is a required field");
        return errors;
      },

      displayDepartmentSelection() {
        return this.notesType === 'product';
      },
      
      disableSaveButton() {
        return this.note === '' || this.type === '' && this.notesType === 'product';
      }
    },

    methods: {  
      ...mapActions('accounts', ['createAccountNote']),
      ...mapActions('productNotes', ['createProductNote']),
      ...mapActions('tickets', ['addTicketNotesByID']),
      ...mapActions('retention', ['createRetentionLeadNote']),

      selectTemplate(id) {
        if(this.note) {
          const action = confirm("Do you want to discard the existing text?");
          if(!action) {
            this.$nextTick(() => {
              this.templateId = this.templateIdRef;
            });
            return;
          }
        }
        const template = this.getNoteTemplates.find(template => template.id === id);
        if(template) {
          this.templateId = template.id
          this.templateIdRef = template.id;
          this.note = template.template;
          this.type = template.note_type;
          this.isTemplate = true;
        }
      },

      async saveNote() {
        this.$v.$touch();
        if (!this.$v.$invalid) {
          this.addNoteLoading = true;
          
          switch(this.notesType) {
            case 'accounts':
              await this.createAccountNote({id: this.id, data: {note: this.note}});
              this.addNoteError = this.getAddAccountNoteError;
              break;
            case 'product':
              await this.createProductNote({id: this.id, data: { note: this.note, type: this.type }});
              this.addNoteError = this.getAddProductNoteError;
              break;
            case 'ticket':
              await this.addTicketNotesByID({tid: this.id, data: {note: this.note}});
              this.addNoteError = this.getAddTicketNoteError;
              break;
            case 'retention':
              await this.createRetentionLeadNote({id: this.id, payload: {note: this.note}});
              this.addNoteError = this.getCreateRetentionLeadNoteError;
              break;
          }
          this.addNoteLoading = false;
          
          if (!this.addNoteError) {
            this.closeDialog();             
          }
        }
      },

      resetForm() {
        this.note = '';
        this.type = '';
        this.templateId = '';
        this.templateIdRef = '';
        this.isTemplate = false;
        this.$v.$reset();       
      },

      closeDialog() {
        this.resetForm();
        this.$emit('closeDialog');
      }
    },

    validations() {
      if (this.notesType === 'product') {
        return {
          type: {required},
          note: {required},
        }
      } 
      
      return {note: {required}}    
    },
  }
</script>

<style ang="scss" scoped>
  .add-note__select {
    max-width: 180px;
  }
</style>
